import React, { useEffect, useId, useRef, useState } from "react";
import { HexAlphaColorPicker } from "react-colorful";
import { UIComponents } from "@/components/ui";
import type { ColorPickerProps } from "./type";
import { HEX_COLOR_REGEX } from "@/features/Site/constants/meterHierarchy.constants";

const ColorPicker = ({
  value,
  onChange,
  visibleColors,
}: ColorPickerProps): React.ReactElement => {
  const [colorPicker, setColorPicker] = useState<boolean>(false);
  const [internalColor, setInternalColor] = useState("#222");
  const colorPickerRef = useRef<HTMLDivElement | null>(null);
  const hexInputId = useId();

  const color = value ?? internalColor;
  const [hexInput, setHexInput] = useState(color);

  const normalizeHexColor = (input: string): string | null => {
    const withHash = input.startsWith("#") ? input : `#${input}`;
    return HEX_COLOR_REGEX.test(withHash) ? withHash.toUpperCase() : null;
  };
  const previewColor = normalizeHexColor(hexInput) ?? color;

  const handleChange = (newColor: string): void => {
    if (onChange) {
      onChange(newColor);
    } else {
      setInternalColor(newColor);
    }
  };

  useEffect(() => {
    if (value !== undefined) {
      setInternalColor(value);
    }
  }, [value]);
  useEffect(() => {
    setHexInput(color);
  }, [color]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target as Node)
      ) {
        setColorPicker(false);
      }
    };
    if (colorPicker) {
      document.addEventListener("click", handleClickOutside);
    }
    return (): void =>
      document.removeEventListener("click", handleClickOutside);
  }, [colorPicker]);

  const swatchColors =
    visibleColors ??
    [
      {
        id: 1,
        color: "#FFFFFF",
      },
      {
        id: 2,
        color: "#000000",
      },
      {
        id: 3,
        color: "#FF5733",
      },
      {
        id: 4,
        color: "#FF8D1A",
      },
      {
        id: 5,
        color: "#28B463",
      },
      {
        id: 6,
        color: "#3498DB",
      },
      {
        id: 7,
        color: "#5DADE2",
      },
      {
        id: 8,
        color: "#2E86C1",
      },
      {
        id: 9,
        color: "#8E44AD",
      },
      {
        id: 10,
        color: "#27AE60",
      },
      {
        id: 11,
        color: "#2B64F4",
      },
      {
        id: 12,
        color: "#138D75",
      },
    ].map((item) => (typeof item === "string" ? item : item.color));

  const handleHexInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setHexInput(event.target.value);
  };

  const handleHexInputBlur = (): void => {
    const normalizedColor = normalizeHexColor(hexInput);
    if (!normalizedColor) {
      setHexInput(color);
      return;
    }
    setHexInput(normalizedColor);
    if (normalizedColor !== color) {
      handleChange(normalizedColor);
    }
  };

  return (
    <div ref={colorPickerRef} className="pt-1.5">
      <p className="mb-2 text-sm font-medium">Select Color</p>
      <div className="space-x-2.5">
        <button
          type="button"
          className="size-5 rounded-full"
          style={{ backgroundColor: color }}
          onClick={() => setColorPicker((prev) => !prev)}
        />
        {/* Selected Color */}
        <button
          className="size-5 rounded-[4px] border border-foreground"
          style={{ backgroundColor: previewColor }}
        />
        {swatchColors.map((swatchColor, index) => (
          <button
            key={`${swatchColor}-${index}`}
            className="size-5 rounded-full border border-border hover:scale-110 transition-transform duration-200"
            style={{ backgroundColor: swatchColor }}
            onClick={() => handleChange(swatchColor)}
          />
        ))}
      </div>
      {colorPicker && (
        <div className="absolute bottom-37 sm:bottom-24 left-6 z-50">
          <div className="color-picker-wrapper space-y-3">
            <HexAlphaColorPicker
              onClick={(e) => e.stopPropagation()}
              color={previewColor}
              onChange={handleChange}
            />
            <div className="space-y-1">
              <label htmlFor={hexInputId} className="text-sm font-medium">
                Hex code
              </label>
              <UIComponents.input
                id={hexInputId}
                value={hexInput}
                onChange={handleHexInputChange}
                onBlur={handleHexInputBlur}
                placeholder="#3498DB"
                maxLength={9}
                className="font-mono uppercase"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
